import { useCallback, useMemo, useReducer, useState } from 'react';
import type { Workflow, WorkflowBranch, WorkflowNode } from '../../../types/backend';
import type { WorkflowDraftSubmission } from './useWorkflowForm';
import {
  addRoute,
  addStage,
  addStep,
  emptyStep,
  moveStage,
  nodesOf,
  removeRoute,
  removeStage,
  removeStep,
  stagesOf,
  toSnakeCase,
  unreachableLayers,
  unreachableMessage,
  updateRoute,
  updateStep,
  validateDraft,
} from '../utils/workflowDraft';
import type { DraftFields, DraftResult, DraftStages } from '../utils/workflowDraft';

interface State extends DraftFields {
  /** Whether the operator typed a name of their own, so it stops following the title. */
  nameEdited: boolean;
  /** The last structural change that was refused, and why. */
  refusal: string | null;
}

type Action =
  | { type: 'field'; field: 'name' | 'title' | 'description'; value: string }
  | { type: 'structure'; apply: (stages: DraftStages) => DraftResult }
  | { type: 'dismissRefusal' };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'field': {
      if (action.field === 'name') return { ...state, name: action.value, nameEdited: true };
      if (action.field === 'title' && !state.nameEdited) {
        return { ...state, title: action.value, name: toSnakeCase(action.value, 80) };
      }
      return { ...state, [action.field]: action.value };
    }
    case 'structure': {
      const result = action.apply(state.stages);
      return 'refused' in result
        ? { ...state, refusal: result.refused }
        : { ...state, stages: result.stages, refusal: null };
    }
    case 'dismissRefusal':
      return { ...state, refusal: null };
    default:
      return state;
  }
};

const init = (workflow: Workflow | null): State =>
  workflow
    ? {
        name: workflow.name,
        title: workflow.title,
        description: workflow.description ?? '',
        stages: stagesOf(workflow.nodes),
        nameEdited: true,
        refusal: null,
      }
    : { name: '', title: '', description: '', stages: [[emptyStep(1)]], nameEdited: false, refusal: null };

// What a save would send, which is what "unsaved" has to mean: a title the draft would store
// trimmed must not read as a change forever after it was stored. `name` is only a new workflow's,
// since a stored one's is fixed; null keeps it out of the comparison rather than comparing it
// against a value nothing can change.
const fingerprint = (
  name: string | null,
  title: string,
  description: string,
  stages: DraftStages,
) => JSON.stringify({ name, title: title.trim(), description, nodes: nodesOf(stages) });

/**
 * The working copy of one draft while it is edited. `workflow` is the stored draft, or null for a
 * new workflow; it seeds the copy once and is then only compared against, so a refetch after a
 * save clears "unsaved changes" without throwing away what is on screen.
 *
 * `hasChanges` is what every way out of the editor is gated on, so it answers one question only:
 * would leaving now lose something the operator typed. An untouched new form answers no.
 */
export const useWorkflowDraft = (workflow: Workflow | null) => {
  const [state, dispatch] = useReducer(reducer, workflow, init);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // What the draft is compared against: the stored version, or the empty form a new workflow
  // starts from. Memoised: serialising every node on each keystroke is the cost avoided.
  const baseline = useMemo(
    () =>
      workflow
        ? fingerprint(null, workflow.title, workflow.description ?? '', stagesOf(workflow.nodes))
        : fingerprint('', '', '', [[emptyStep(1)]]),
    [workflow],
  );

  // Memoised for the same reason the baseline is: one serialisation per keystroke, not two.
  const hasChanges = useMemo(
    () =>
      fingerprint(
        workflow ? null : state.name,
        state.title,
        state.description,
        state.stages,
      ) !== baseline,
    [baseline, workflow, state.name, state.title, state.description, state.stages],
  );

  const errors = hasAttemptedSave ? validateDraft(state, workflow === null) : [];
  // Shown as the structure changes, not only on save: a move or a re-pointed route that strands a
  // stage is easiest to undo at the moment it happens. Once saving was attempted the same
  // sentence is already among the errors.
  const unreachable = hasAttemptedSave ? [] : unreachableLayers(state.stages).map(unreachableMessage);

  // Memoised: every stage and step control below dispatches through it.
  const structure = useCallback(
    (apply: (stages: DraftStages) => DraftResult) => dispatch({ type: 'structure', apply }),
    [],
  );

  return {
    name: state.name,
    title: state.title,
    description: state.description,
    stages: state.stages,
    refusal: state.refusal,
    errors,
    unreachable,
    hasChanges,
    setName: (value: string) => dispatch({ type: 'field', field: 'name', value }),
    setTitle: (value: string) => dispatch({ type: 'field', field: 'title', value }),
    setDescription: (value: string) => dispatch({ type: 'field', field: 'description', value }),
    dismissRefusal: () => dispatch({ type: 'dismissRefusal' }),
    addStage: () => structure(addStage),
    moveStage: (from: number, to: number) => structure((s) => moveStage(s, from, to)),
    removeStage: (index: number) => structure((s) => removeStage(s, index)),
    addStep: (stageIndex: number) => structure((s) => addStep(s, stageIndex)),
    removeStep: (stageIndex: number, stepIndex: number) =>
      structure((s) => removeStep(s, stageIndex, stepIndex)),
    updateStep: (stageIndex: number, stepIndex: number, patch: Partial<WorkflowNode>) =>
      structure((s) => updateStep(s, stageIndex, stepIndex, patch)),
    addRoute: (stageIndex: number, stepIndex: number) =>
      structure((s) => addRoute(s, stageIndex, stepIndex)),
    updateRoute: (
      stageIndex: number,
      stepIndex: number,
      routeIndex: number,
      patch: Partial<WorkflowBranch>,
    ) => structure((s) => updateRoute(s, stageIndex, stepIndex, routeIndex, patch)),
    removeRoute: (stageIndex: number, stepIndex: number, routeIndex: number) =>
      structure((s) => removeRoute(s, stageIndex, stepIndex, routeIndex)),
    /** The draft to save, or null when a cheap rule fails; the failures are then in `errors`. */
    submission: (): WorkflowDraftSubmission | null => {
      setHasAttemptedSave(true);
      if (validateDraft(state, workflow === null).length > 0) return null;
      return {
        ...(workflow === null ? { name: state.name } : {}),
        title: state.title.trim(),
        description: state.description,
        nodes: nodesOf(state.stages),
      };
    },
  };
};
